import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Badge,
  TextField,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachIcon,
  LocalHospital as ClinicIcon
} from '@mui/icons-material';
import clientDashboardService from '@services/clientDashboardService';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import EmptyState from '@shared/EmptyState';

interface Thread {
  id: string;
  clinicName: string;
  patientName?: string;
  last_message_at: string;
  unread_count: number;
}

interface Message {
  id: string;
  sender_type: string;
  sender_id: string;
  senderFirstName?: string;
  senderLastName?: string;
  body: string;
  created_at: string;
  read_at?: string;
}

const ClientMessages: React.FC = () => {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  useEffect(() => {
    loadThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      loadMessages(selectedThread.id);
    }
  }, [selectedThread]);

  const loadThreads = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientDashboardService.getThreads();
      const data = Array.isArray(response) ? response : (response as any)?.data || [];
      setThreads(data);
    } catch (err) {
      console.error('Error loading threads:', err);
      setError('Eroare la incarcarea conversatiilor');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (threadId: string) => {
    try {
      const response = await clientDashboardService.getThreadMessages(threadId);
      const data = Array.isArray(response) ? response : (response as any)?.data || [];
      setMessages(data);
      
      // Marcheaza mesajele ca citite
      await clientDashboardService.markThreadAsRead(threadId);
      
      // Actualizeaza unread count local
      setThreads(prev => prev.map(t => 
        t.id === threadId ? { ...t, unread_count: 0 } : t
      ));
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Eroare la incarcarea mesajelor');
    }
  };

  const handleSendMessage = async () => {
    if (!selectedThread || !newMessage.trim()) return;

    try {
      setSending(true);
      setError(null);
      
      await clientDashboardService.sendMessage(selectedThread.id, {
        body: newMessage.trim()
      });

      setNewMessage('');
      await loadMessages(selectedThread.id);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Eroare la trimiterea mesajului');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        💬 Mesajele Mele
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {threads.length === 0 ? (
        <EmptyState
          icon={<SendIcon sx={{ fontSize: 64, color: 'text.secondary' }} />}
          title="Nicio conversatie"
          description="Conversatiile cu clinicile vor aparea aici"
        />
      ) : (
        <Box display="flex" gap={2} sx={{ height: 'calc(100vh - 250px)' }}>
          {/* Threads List */}
          <Paper sx={{ width: 350, overflow: 'auto', borderRadius: 2 }}>
            <List sx={{ p: 0 }}>
              {threads.map((thread, index) => (
                <React.Fragment key={thread.id}>
                  <ListItemButton
                    selected={selectedThread?.id === thread.id}
                    onClick={() => setSelectedThread(thread)}
                    sx={{
                      py: 2,
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected'
                      }
                    }}
                  >
                    <ListItemAvatar>
                      <Badge badgeContent={thread.unread_count} color="error">
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <ClinicIcon />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" sx={{ fontWeight: thread.unread_count > 0 ? 600 : 400 }}>
                          {thread.clinicName}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          {thread.patientName && (
                            <Chip label={thread.patientName} size="small" sx={{ fontSize: '0.7rem', height: 20, mt: 0.5 }} />
                          )}
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {format(new Date(thread.last_message_at), 'dd MMM yyyy', { locale: ro })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  {index < threads.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>

          {/* Messages View */}
          <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
            {selectedThread ? (
              <>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedThread.clinicName}
                  </Typography>
                  {selectedThread.patientName && (
                    <Typography variant="caption" color="text.secondary">
                      Conversatie despre: {selectedThread.patientName}
                    </Typography>
                  )}
                </Box>

                {/* Messages */}
                <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {messages.map((message) => {
                    const isFromMe = message.sender_type === 'patient';
                    
                    return (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          justifyContent: isFromMe ? 'flex-end' : 'flex-start'
                        }}
                      >
                        <Card
                          sx={{
                            maxWidth: '70%',
                            bgcolor: isFromMe ? 'primary.main' : 'background.paper',
                            color: isFromMe ? 'white' : 'text.primary',
                            borderRadius: 2
                          }}
                        >
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            {!isFromMe && (
                              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                {message.senderFirstName} {message.senderLastName}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {message.body}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                opacity: 0.7,
                                textAlign: 'right'
                              }}
                            >
                              {format(new Date(message.created_at), 'HH:mm', { locale: ro })}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Box>
                    );
                  })}
                </Box>

                {/* Input */}
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box display="flex" gap={1}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Scrie un mesaj..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={sending}
                      size="small"
                    />
                    <IconButton
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={!newMessage.trim() || sending}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' },
                        '&:disabled': { bgcolor: 'action.disabledBackground' }
                      }}
                    >
                      {sending ? <CircularProgress size={24} /> : <SendIcon />}
                    </IconButton>
                  </Box>
                </Box>
              </>
            ) : (
              <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
                <Typography variant="body1" color="text.secondary">
                  Selecteaza o conversatie pentru a vedea mesajele
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default ClientMessages;

